import { Typography } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';

import { APP_LAYOUT_HEIGHT } from '@/config';

type Route = {
  path: string;
  name: string;
  disabled: boolean;
};

type RouteGroup = {
  header: {
    name: string;
  };
  subRoutes: Route[];
};

type SettingsSidebarProps = {
  routes: RouteGroup[];
};

const SIDEBAR_EXPANDED_WIDTH = 280;

const sidebarContainerStyle: React.CSSProperties = {
  width: `${SIDEBAR_EXPANDED_WIDTH}px`,
  height: `calc(100vh - ${APP_LAYOUT_HEIGHT.HEADER}px)`,
  borderRight: '0.5px solid grey',
  backgroundColor: '#fff',
  bottom: 0,
  position: 'absolute',
  overflowY: 'auto',
};

const headerStyle = {
  borderTop: '1px solid grey',
  borderBottom: '1px solid grey',
  padding: '8px 10px',
  fontWeight: 'bold',
  fontSize: 12,
};

const subRouteStyle = {
  padding: '8px 0',
  paddingLeft: '10px',
  display: 'block',
  transition: 'background-color 0.3s, border-right 0.3s',
};

const disabledStyle = {
  ...subRouteStyle,
  color: '#B0B0B0',
  cursor: 'not-allowed',
  opacity: 0.5,
};

const activeStyle = {
  backgroundColor: '#e5f2f2',
  borderRight: '3px solid #007a7a',
};

export const SettingsSidebar = ({ routes }: SettingsSidebarProps) => {
  const location = useLocation();
  const basePath = '/settings';

  const renderHeader = (name: string) => (
    <Typography variant="h6" style={headerStyle}>
      {name}
    </Typography>
  );

  const renderSubRoute = (subRoute: Route) => {
    const isSelected = location.pathname === `${basePath}${subRoute.path}`;

    if (subRoute.disabled) {
      return (
        <div
          key={subRoute.path}
          style={{ ...disabledStyle, ...(isSelected ? activeStyle : {}) }}
        >
          <Typography variant="h6" fontSize={12}>
            {subRoute.name}
          </Typography>
        </div>
      );
    }

    return (
      <Link
        key={subRoute.path}
        to={`${basePath}${subRoute.path}`}
        style={{
          ...subRouteStyle,
          ...(isSelected ? activeStyle : {}),
          color: '#4A555F',
          textDecoration: 'none',
        }}
        onMouseOver={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = '#e5f2f2';
            e.currentTarget.style.borderRight = '3px solid #007a7a';
          }
        }}
        onMouseOut={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderRight = 'none';
          }
        }}
      >
        <Typography variant="h6" fontSize={12}>
          {subRoute.name}
        </Typography>
      </Link>
    );
  };

  return (
    <div style={sidebarContainerStyle}>
      <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
        {routes.map((route) => (
          <li key={route.header.name}>
            {renderHeader(route.header.name)}
            <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
              {route.subRoutes.map(renderSubRoute)}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};
